import { useMemo } from "react";
import ColorBadge from "@/components/shared/ColorBadge";
import { useDropdownData } from "@/hooks/useDropdownData";
import { workPillColorFor } from "@/lib/constants";
import { useSubsheets } from "@/hooks/useSubsheets";
import { firstVisibleSubsheetId } from "@/lib/backlogUtils";
import { getDisplayName } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import type { BacklogItem } from "@/types/backlog";
import type { Mention } from "@/hooks/useMentions";

const DAY_MS = 24 * 60 * 60 * 1000;

/** "new" under 2 days, then 3d, then 1w/2w — the age is a scent, not a report. */
export const ageLabel = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);
  if (days < 2) return "new";
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
};

export const blockedDays = (iso: string) => Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS));

const DEPT_TILE_COLORS = [
  "bg-sky-500",
  "bg-violet-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-teal-500",
];

export interface WorkMeta {
  statusMap: Map<string, { label: string; color: string }>;
  deptIndex: Map<string, { name: string; i: number }>;
}

/** Lookup maps a work list resolves rows against — built once per list, not per row. */
export const useWorkMeta = (): WorkMeta => {
  const { data: dropdownData } = useDropdownData();
  const { data: subsheets = [] } = useSubsheets();

  const statusMap = useMemo(() => {
    const map = new Map<string, { label: string; color: string }>();
    dropdownData?.statuses?.forEach((s) => map.set(s.id, { label: s.label, color: s.color }));
    return map;
  }, [dropdownData?.statuses]);

  const deptIndex = useMemo(() => {
    const map = new Map<string, { name: string; i: number }>();
    subsheets.forEach((s, i) => map.set(s.id, { name: s.name, i }));
    return map;
  }, [subsheets]);

  return { statusMap, deptIndex };
};

const DeptTile = ({ item, meta }: { item: BacklogItem; meta: WorkMeta }) => {
  const sheetId = firstVisibleSubsheetId(item.subsheet_ids, meta.deptIndex);
  const dept = sheetId ? meta.deptIndex.get(sheetId) : undefined;
  if (!dept) return <span className="h-5 w-5 shrink-0 rounded bg-muted" aria-hidden />;
  return (
    <span
      title={dept.name}
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold uppercase text-white",
        DEPT_TILE_COLORS[dept.i % DEPT_TILE_COLORS.length],
      )}
    >
      {dept.name.charAt(0)}
    </span>
  );
};

interface WorkRowProps {
  item: BacklogItem;
  meta: WorkMeta;
  onOpen: (item: BacklogItem) => void;
  /** "blocked" swaps the age label for a red blocked-for-N-days counter. */
  aging?: "blocked";
}

/** One line of personal work — Home's groups and the /focus screens share it. */
export const WorkRow = ({ item, meta, onOpen, aging }: WorkRowProps) => {
  const status = item.status_id ? meta.statusMap.get(item.status_id) : null;
  return (
    <div
      onClick={() => onOpen(item)}
      className="flex cursor-pointer items-center gap-3 border-t border-border/50 px-3 py-2 text-[13.5px] transition-colors first:border-t-0 hover:bg-[hsl(var(--table-row-hover))]"
    >
      <DeptTile item={item} meta={meta} />
      <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground/70">#{item.ticket_number}</span>
      <span className="min-w-0 flex-1 truncate font-medium">{item.task}</span>
      {aging === "blocked" ? (
        // `--blocked-fg`, not `red-600`/`red-400` (see the token in index.css):
        // the counter and the Blocked pill next to it were two saturated reds
        // stating one fact, which is a register above what a five-row list on a
        // dashboard should be shouting.
        <span className="shrink-0 text-[11px] font-bold text-[hsl(var(--blocked-fg))]">
          blocked {blockedDays(item.updated_at)}d
        </span>
      ) : (
        <span className="shrink-0 text-[11px] font-bold text-muted-foreground/70">{ageLabel(item.updated_at)}</span>
      )}
      {/* The stored status colour, pulled into the palette's pastel band where
          it is heavier than the band (Blocked ships as #EF4444). Every other
          status in the starter set is already pastel and passes through
          untouched — see workPillColorFor. */}
      {status && (
        <ColorBadge label={status.label} color={workPillColorFor(status.color)} id={item.status_id || undefined} />
      )}
    </div>
  );
};

interface MentionRowProps {
  mention: Mention;
  onOpen: (mention: Mention) => void;
}

/** An unread mention rendered in the same voice as a work row. */
export const MentionRow = ({ mention, onOpen }: MentionRowProps) => (
  <div
    onClick={() => onOpen(mention)}
    className="flex cursor-pointer items-center gap-3 border-t border-border/50 px-3 py-2 text-[13.5px] transition-colors first:border-t-0 hover:bg-[hsl(var(--table-row-hover))]"
  >
    {/* --primary is navy-on-near-black in dark mode (the .th-active problem,
        src/index.css), so every navy accent in these rows points at the ring
        cyan there instead. */}
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-secondary text-[11px] font-extrabold text-primary dark:text-[hsl(var(--ring))]">
      @
    </span>
    <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground/70">
      #{mention.comment?.backlog_item?.ticket_number}
    </span>
    <span className="min-w-0 flex-1 truncate font-medium">
      {mention.comment?.user ? getDisplayName(mention.comment.user) : "Someone"} mentioned you
      {mention.comment?.backlog_item?.task && (
        <span className="text-muted-foreground"> · {mention.comment.backlog_item.task}</span>
      )}
    </span>
    <span className="shrink-0 text-[11px] font-bold text-muted-foreground/70">{ageLabel(mention.created_at)}</span>
    <span className="shrink-0 rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-bold text-primary dark:text-[hsl(var(--ring))]">
      Reply
    </span>
  </div>
);
